import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { sitesApi, type Site } from '@/api/endpoints';
import { useSiteLock } from './useSiteLock';

/**
 * 「当前在哪个工地」的唯一来源（CR-008）。
 *
 * 以前每个业务页面各自揣着一个 `useState('')` 的工地下拉框，彼此不同步 ——
 * 在图纸页选了工地 1，切到任务页又回到默认工地。工地现在是路由的父级，
 * 页面只从这里取，界面上永远只有一个答案。
 *
 * 接口本来就是按工地分的（`sites/:siteId/...`），权限也在服务端按
 * 「是不是这个工地的在职成员」卡着。这个 hook 只负责让前端的层次和后端对齐。
 *
 * **「没有工地」一律用 null 表达，不用 0，也不用空串**（CR-019）。
 * 工地 id 现在是整数，`0` 会被 `Boolean(siteId)`、`siteId ? ... : ...`
 * 这类写法当成「没选」—— 项目里这种写法有十来处，靠人去记哪处该改是赌运气。
 * null 让编译器替我们盯着。
 */

// 和工人端共用同一个键：管理端和手机端对「当前工地」只应该有一个答案
const LAST_SITE_KEY = 'construction.lastSiteId';

export function useSites() {
  return useQuery({ queryKey: ['sites'], queryFn: () => sitesApi.list() });
}

export interface CurrentSite {
  /** 路由里的工地 id。不在当前用户可见范围内时，site 会是 null */
  siteId: number | null;
  site: Site | null;
  isLoading: boolean;
  /** 当前用户能看到的工地。服务端只返回他参与的那些 */
  sites: Site[];
}

export function useCurrentSite(): CurrentSite {
  const { siteId: routeSiteId } = useParams<{ siteId: string }>();
  const sites = useSites();
  const list = sites.data ?? [];

  // 路由参数永远是字符串。'abc' 和 '' 都不是工地 id，转不出整数就当没带。
  const routeId = parseSiteId(routeSiteId);

  // 路由里带了工地就必须用它，找不到说明这人没权限看 —— 保持 null，
  // 让页面显示「无权访问」而不是偷偷换成别的工地。
  //
  // 路由里没带的，是手机端的管理者外壳（底部 Tab 的链接不带工地段），
  // 这时落回记住的那个工地，管理端页面组件不用改就能在两套外壳里复用。
  const site =
    routeId != null ? (list.find((s) => s.id === routeId) ?? null) : pickDefaultSite(list);

  useEffect(() => {
    if (site) rememberSite(site.id);
  }, [site]);

  return {
    siteId: site?.id ?? routeId,
    site,
    isLoading: sites.isLoading,
    sites: list,
  };
}

/** 字符串转工地 id。不是纯数字就返回 null —— Number('') 是 0，不能用 */
export function parseSiteId(raw: string | null | undefined): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isSafeInteger(n) ? n : null;
}

export function rememberSite(siteId: number): void {
  try {
    localStorage.setItem(LAST_SITE_KEY, String(siteId));
  } catch {
    /* 隐私模式下存不下，退化成每次进第一个工地 */
  }
}

export function readRememberedSite(): number | null {
  try {
    return parseSiteId(localStorage.getItem(LAST_SITE_KEY));
  } catch {
    return null;
  }
}

/**
 * 决定「没指定工地时该进哪个」：上次进的那个，没有就第一个。
 * 记住的工地可能已经归档或者人已经被移出，所以必须在可见列表里校验一遍。
 */
export function pickDefaultSite(sites: Site[]): Site | null {
  if (sites.length === 0) return null;
  const remembered = readRememberedSite();
  return sites.find((s) => s.id === remembered) ?? sites[0] ?? null;
}

/**
 * 工人端的「当前工地」。
 *
 * 手机端没把工地放进 URL —— 工人几乎只在一个工地干活，多一层路径只会让
 * 底部 Tab 的链接变复杂。改成记住上次的选择，首页上给一个切换器。
 * 存的键和管理端是同一个，所以两边不会各说各话。
 *
 * **打了上班卡就锁定**（CR-015）：任务、图纸、隐蔽工程都跟着锁在那个工地上，
 * 直到打下班卡。锁定放在这个 hook 里而不是每个页面里 —— CLAUDE.md 约束 7
 * 已经把它定成唯一入口，放在页面里就是七个地方各写一遍，必然漏掉一个。
 *
 * 没有豁免。CR-015 曾让采购豁免锁定（一次为几个工地买料），CR-042 由客户推翻：
 * 「只能为当前工地买材料。如果还需要为其他工地买材料，可以在备注里填写」。
 */
export function useMobileSite() {
  const sites = useSites();
  const list = sites.data ?? [];
  const [chosen, setChosen] = useState<number | null>(() => readRememberedSite());
  const { lockedSiteId, shift } = useSiteLock();

  /**
   * 网页端也会渲染这些页面（CR-040 / CR-042：日报、采购、隐蔽工程挂在 /sites/:siteId/... 下）。
   * 那时**工地由路由决定**，和网页端其他页面一样（CR-008），页面里的工地下拉不出现 ——
   * 左侧导航顶上已经有一个切换器，两个切换器各说各的是 CR-008 之前的老毛病。
   *
   * 优先级：路由 `:siteId` > 在班锁定 > 记住的。网页端左侧导航选了哪个就是哪个（CR-042：
   * 选定之后一切在这个工地之下）；手机端没有路由工地，锁定照旧优先（约束 9）。
   */
  const { siteId: rawRouteSiteId } = useParams<{ siteId: string }>();
  const routeSiteId = parseSiteId(rawRouteSiteId);

  const locked = routeSiteId === null && lockedSiteId != null;
  const effectiveId = routeSiteId ?? (locked ? lockedSiteId : chosen);

  // 锁定的工地可能已归档、或者人已被移出 —— 那时它不在 list 里。
  // 这种时候绝不能落回 pickDefaultSite（会把人偷偷换到别的工地去），
  // 用班次接口带回来的那份工地副本，好歹能让他把这个班闭上。
  // 路由里带了工地却不在可见列表里，同理保持 null，不偷偷换。
  const site =
    (effectiveId != null ? list.find((s) => s.id === effectiveId) : undefined) ??
    (locked ? (shift?.site ?? null) : routeSiteId !== null ? null : pickDefaultSite(list));

  useEffect(() => {
    if (!locked && routeSiteId === null && site && site.id !== chosen) setChosen(site.id);
  }, [site, chosen, locked, routeSiteId]);

  return {
    site,
    siteId: site?.id ?? null,
    sites: routeSiteId !== null ? list.filter((s) => s.id === routeSiteId) : list,
    isLoading: sites.isLoading,
    /** 在班期间工地锁死。界面上要换成静态文字，不要留一个点了没反应的下拉框 */
    locked,
    setSite: (id: number) => {
      if (locked || routeSiteId !== null) return;
      rememberSite(id);
      setChosen(id);
    },
  };
}
